import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { resetSiteSpecificData } from "./GlobalActions";

import { ITrackersState, TrackersStateEnum } from "../../trackers/TrackersModels";
import { getAllLatestTrackersAsync } from "../../trackers/TrackersAPI";
import { DevicesMap, TrackerName } from "../../sites/SiteModels";
import { beginRequest, completeRequest, rejectRequest } from "./RequestState";
import { LoadingState } from "../LoadingState";

const defaultTrackersState: ITrackersState = {
  trackersStatus: {},
  loading: false,
  loadingBySite: {},
  requestsBySite: {},
  selectedTrackersIds: [],
};

export const multiSitesTrackersSlice = createSlice({
  name: "multiSitesTrackers",
  initialState: defaultTrackersState,
  reducers: {
    onFieldStateDisconnected: (
      state,
      action: PayloadAction<{
        siteId: string;
        trackerIds: number[];
        trackerNames: DevicesMap<TrackerName | null | undefined>;
      }>
    ) => {
      const { siteId, trackerIds, trackerNames } = action.payload;
      if (state.requestsBySite) {
        delete state.requestsBySite[siteId];
      }
      if (state.loadingBySite) {
        state.loadingBySite[siteId] = false;
        state.loading = Object.values(state.loadingBySite).some(Boolean);
      }
      const dateNow = Date.now();
      state.trackersStatus[siteId] = trackerIds.map((trackerId) => ({
        updateTime: dateNow,
        id: trackerId,
        name: trackerNames[trackerId] ?? trackerId.toString(),
        currentState: TrackersStateEnum.NotAvailable,
        hasForcedElevation: false,
      }));
    },
    setSelectedTrackers: (
      state,
      action: PayloadAction<{
        selectedTrackersIds: number[];
      }>
    ) => {
      state.selectedTrackersIds = action.payload.selectedTrackersIds;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTrackerStatus.pending, (state, action) => {
        state.requestsBySite ??= {};
        state.loadingBySite ??= {};
        const request = (state.requestsBySite[action.meta.arg] ??= { loadingState: LoadingState.None });
        beginRequest(request, action.meta.requestId);
        state.loadingBySite[action.meta.arg] = true;
        state.loading = true;
      })
      .addCase(fetchTrackerStatus.fulfilled, (state, action) => {
        const { site, trackerResponse } = action.payload;
        const request = state.requestsBySite?.[site];
        if (!request || !completeRequest(request, action.meta.requestId)) {
          return;
        }
        state.trackersStatus[site] = trackerResponse;
        state.loadingBySite![site] = false;
        state.loading = Object.values(state.loadingBySite!).some(Boolean);
      })
      .addCase(fetchTrackerStatus.rejected, (state, action) => {
        const request = state.requestsBySite?.[action.meta.arg];
        if (request && rejectRequest(request, action)) {
          state.loadingBySite![action.meta.arg] = false;
          state.loading = Object.values(state.loadingBySite!).some(Boolean);
        }
      })
      .addCase(resetSiteSpecificData, (state) => {
        state.selectedTrackersIds = [];
      });
  },
});

export const fetchTrackerStatus = createAsyncThunk(
  "multiSitesTrackers/fetchStatus",
  async (siteId: string) => {
    const response = await getAllLatestTrackersAsync(siteId);
    return {
      site: siteId,
      trackerResponse: response,
    };
  }
);

export const { onFieldStateDisconnected } = multiSitesTrackersSlice.actions;
export const { setSelectedTrackers } = multiSitesTrackersSlice.actions;
export const multiSitesTrackersReducer = multiSitesTrackersSlice.reducer;
